import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';

import { exportAiSpendRuns } from '../../services/aiSpendService';
import { downloadBlob, readBlobError } from '../../utils/helpers/download';
import { formatCount } from '../../utils/helpers/aiSpendFormat';

/**
 * Export through axios, never `window.open`.
 *
 * `window.open` leaves the axios instance behind, so the bearer token never
 * gets attached and this admin-only endpoint would answer 401. Going through
 * the instance also means a token refresh mid-download still returns a blob,
 * because the retry reuses the original config including `responseType`.
 */
export default function ExportRunsButton({ params, rowCount, rowLimit = 100000 }) {
  const [busy, setBusy] = useState(false);
  const tooMany = rowCount > rowLimit;

  const handleExport = async () => {
    setBusy(true);
    try {
      const { blob, filename } = await exportAiSpendRuns({ params });
      downloadBlob(blob, filename);
      toast.success('Export downloaded.');
    } catch (error) {
      toast.error((await readBlobError(error)) || 'Could not export these runs.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={busy || tooMany}
      title={
        tooMany
          ? `Narrow the date range — the export is capped at ${formatCount(rowLimit)} rows`
          : undefined
      }
      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
    >
      <ArrowDownTrayIcon className="h-4 w-4" />
      {busy ? 'Preparing…' : 'Export CSV'}
    </button>
  );
}
